/**
 * WP Cluster Fit Classifier — LLM klasyfikacja scope wpisu WP wzgledem topical mapy.
 *
 * 3 labele: in_scope / adjacent / off_topic. Prompt: thematic_prompts.step='wp_cluster_fit'
 * (gpt-4.1-mini, temp 0.2, structured output strict).
 *
 * Hybrid shortcut (bez LLM) dla wpisow zmatchowanych przez SRO Matcher:
 *   thematic_nodes WHERE map_id=X AND matched_wp_post_id=post.id
 *     AND match_status IN (matched_keep, matched_refurbish, partial)
 *   -> cluster_fit='in_scope', method='auto_matched', confidence=1.0
 *
 * Pozostale wpisy (unmatched/skip_manual/null) -> LLM classification.
 *
 * Manual override (cluster_fit_method='manual_override') NIGDY nie jest nadpisywany
 * przez auto ani LLM (nawet przy forceReclassify=true).
 *
 * Koszt: ~$0.0015 per wpis (krotszy prompt niz Quality Audit, same 3-label output).
 */

import { supabase } from "@/integrations/supabase/client";
import { loadPrompt, fillTemplate } from "@/services/llm/promptLoader";
import { openaiProvider } from "@/services/llm/openaiProvider";
import { logPublic, logDebug, logError } from "@/services/contentEngine/contentLogger";

const PREFIX = "[CLUSTER-FIT]";
const DEFAULT_CONCURRENCY = 10;
const MAX_CONCURRENCY = 30;
const RETRY_MAX = 1;

export type ClusterFitLabel = "in_scope" | "adjacent" | "off_topic";
export type ClusterFitMethod = "auto_matched" | "llm" | "manual_override";

const CLUSTER_FIT_SCHEMA: Record<string, unknown> = {
  type: "object",
  additionalProperties: false,
  required: ["classification", "confidence", "reason"],
  properties: {
    classification: { type: "string", enum: ["in_scope", "adjacent", "off_topic"] },
    confidence: { type: "number", minimum: 0, maximum: 1 },
    reason: { type: "string" },
  },
};

interface ClassifiableRow {
  id: string;
  title: string | null;
  content_plaintext: string | null;
  extracted_entity: string | null;
  extracted_attribute: string | null;
  cluster_fit_method: ClusterFitMethod | null;
  cluster_fit_classified_at: string | null;
}

interface MapContext {
  mapId: string;
  websiteId: string;
  macroStatement: string;
  seedNames: string[];
  entitiesSample: string[];
}

export interface ClusterFitResultSingle {
  success: boolean;
  classification?: ClusterFitLabel;
  confidence?: number;
  reason?: string;
  compiled_prompt?: string;
  response_text?: string;
  tokens_in: number;
  tokens_out: number;
  cost_usd: number;
  error?: string;
}

export interface ClusterFitStats {
  processed: number;
  auto_matched: number;
  llm_classified: number;
  in_scope: number;
  adjacent: number;
  off_topic: number;
  failed: number;
  cost_usd: number;
  tokens_in: number;
  tokens_out: number;
  duration_ms: number;
}

export interface ClusterFitBatchOptions {
  onProgress?: (done: number, total: number, costUsd: number) => void;
  onLog?: (
    type: "info" | "success" | "error" | "stage",
    message: string,
    debug?: Record<string, unknown>,
  ) => void;
  concurrency?: number;
  forceReclassify?: boolean;
}

async function loadMapContext(mapId: string): Promise<MapContext> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: map, error: mapErr } = await (supabase as any)
    .from("thematic_maps")
    .select("id, website_id, macro_statement")
    .eq("id", mapId)
    .maybeSingle();

  if (mapErr) throw mapErr;
  if (!map) throw new Error(`Mapa ${mapId} nie istnieje`);
  if (!map.website_id) throw new Error(`Mapa ${mapId} nie ma przypisanego website_id`);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: seeds } = await (supabase as any)
    .from("thematic_seeds")
    .select("name, priority")
    .eq("map_id", mapId)
    .order("priority", { ascending: false });

  const seedNames = ((seeds || []) as Array<{ name: string | null }>)
    .map((s) => s.name?.trim())
    .filter((n): n is string => Boolean(n && n.length));

  // Top 30 entities by frequency in thematic_nodes.required_entity_ids
  // (liczymy czestosc w kodzie — thematic_entities nie ma occurrence_count)
  let entitiesSample: string[] = [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: nodes } = await (supabase as any)
    .from("thematic_nodes")
    .select("required_entity_ids")
    .eq("map_id", mapId);

  const counts = new Map<string, number>();
  for (const n of (nodes || []) as Array<{ required_entity_ids: string[] | null }>) {
    const ids = Array.isArray(n.required_entity_ids) ? n.required_entity_ids : [];
    for (const id of ids) {
      counts.set(id, (counts.get(id) ?? 0) + 1);
    }
  }

  const topIds = [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 30)
    .map(([id]) => id);

  if (topIds.length > 0) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data: entities } = await (supabase as any)
      .from("thematic_entities")
      .select("id, canonical_name")
      .in("id", topIds);

    const byId = new Map<string, string>(
      ((entities || []) as Array<{ id: string; canonical_name: string }>).map((e) => [
        e.id,
        e.canonical_name,
      ]),
    );
    entitiesSample = topIds.map((id) => byId.get(id) ?? "").filter(Boolean);
  }

  if (entitiesSample.length === 0) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data: fallback } = await (supabase as any)
      .from("thematic_entities")
      .select("canonical_name")
      .eq("map_id", mapId)
      .limit(30);
    entitiesSample = ((fallback || []) as Array<{ canonical_name: string }>).map(
      (e) => e.canonical_name,
    );
  }

  return {
    mapId,
    websiteId: map.website_id,
    macroStatement: map.macro_statement || "",
    seedNames,
    entitiesSample,
  };
}

async function loadMatchedPostIdsForMap(mapId: string): Promise<Set<string>> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data } = await (supabase as any)
    .from("thematic_nodes")
    .select("matched_wp_post_id")
    .eq("map_id", mapId)
    .not("matched_wp_post_id", "is", null)
    .in("match_status", ["matched_keep", "matched_refurbish", "partial"]);

  return new Set(
    ((data || []) as Array<{ matched_wp_post_id: string }>).map((r) => r.matched_wp_post_id),
  );
}

async function classifyOne(
  row: ClassifiableRow,
  ctx: MapContext,
  template: string,
  modelId: string,
  temperature: number,
): Promise<ClusterFitResultSingle> {
  const prompt = fillTemplate(template, {
    map_macro_statement: ctx.macroStatement || "(brak macro statement)",
    seed_names_list: ctx.seedNames.length > 0 ? ctx.seedNames.join(", ") : "(brak seedow)",
    map_entities_sample:
      ctx.entitiesSample.length > 0 ? ctx.entitiesSample.join(", ") : "(brak entities)",
    wp_title: row.title || "",
    extracted_entity: row.extracted_entity || "UNKNOWN",
    extracted_attribute: row.extracted_attribute || "UNKNOWN",
    content_excerpt: (row.content_plaintext || "").slice(0, 300),
  });

  let lastError: string | undefined;
  let totalTokensIn = 0;
  let totalTokensOut = 0;
  let totalCost = 0;
  let lastResponseText = "";

  for (let attempt = 0; attempt <= RETRY_MAX; attempt++) {
    try {
      const response = await openaiProvider.call(prompt, {
        model: modelId,
        temperature,
        jsonSchema: CLUSTER_FIT_SCHEMA,
      });
      totalTokensIn += response.tokensInput;
      totalTokensOut += response.tokensOutput;
      totalCost += response.costUsd ?? 0;
      lastResponseText = response.text;

      const parsed = JSON.parse(response.text) as {
        classification: ClusterFitLabel;
        confidence: number;
        reason: string;
      };

      return {
        success: true,
        classification: parsed.classification,
        confidence: parsed.confidence,
        reason: parsed.reason,
        compiled_prompt: prompt,
        response_text: lastResponseText,
        tokens_in: totalTokensIn,
        tokens_out: totalTokensOut,
        cost_usd: totalCost,
      };
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      lastError = msg;
      if (attempt < RETRY_MAX) {
        logDebug(PREFIX, `Classify error attempt ${attempt + 1}: ${msg} — retrying`);
        continue;
      }
    }
  }

  return {
    success: false,
    error: lastError ?? "unknown",
    compiled_prompt: prompt,
    response_text: lastResponseText,
    tokens_in: totalTokensIn,
    tokens_out: totalTokensOut,
    cost_usd: totalCost,
  };
}

/**
 * Klasyfikacja batch dla wszystkich wpisow powiazanych z website mapy.
 * Hybrid shortcut (auto_matched) dla wpisow zmatchowanych, LLM dla pozostalych.
 */
export async function classifyClusterFitForMap(
  mapId: string,
  options: ClusterFitBatchOptions = {},
): Promise<ClusterFitStats> {
  const t0 = Date.now();
  const concurrency = Math.max(1, Math.min(MAX_CONCURRENCY, options.concurrency ?? DEFAULT_CONCURRENCY));
  const forceReclassify = options.forceReclassify ?? false;

  const ctx = await loadMapContext(mapId);

  // GUARD: eligible (sro done, not manual_override)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { count: eligibleCount } = await (supabase as any)
    .from("wp_posts")
    .select("id", { count: "exact", head: true })
    .eq("website_id", ctx.websiteId)
    .not("sro_extracted_at", "is", null)
    .or("cluster_fit_method.is.null,cluster_fit_method.neq.manual_override");

  if ((eligibleCount ?? 0) === 0) {
    throw new Error(
      "Brak wpisów z SRO extraction. Odpal najpierw 'Ekstrakcja SRO' w panelu Istniejących wpisów.",
    );
  }

  const prompt = await loadPrompt("wp_cluster_fit");
  if (!prompt) {
    throw new Error("Prompt wp_cluster_fit nie jest aktywny w bazie");
  }

  // Load eligible wpisy
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let q = (supabase as any)
    .from("wp_posts")
    .select(
      "id, title, content_plaintext, extracted_entity, extracted_attribute, cluster_fit_method, cluster_fit_classified_at",
    )
    .eq("website_id", ctx.websiteId)
    .not("sro_extracted_at", "is", null);

  if (!forceReclassify) {
    q = q.is("cluster_fit_classified_at", null);
  }

  const { data, error } = await q;
  if (error) throw error;

  const allRows = (data || []) as ClassifiableRow[];
  // Zawsze skip manual_override (nawet przy force)
  const rows = allRows.filter((r) => r.cluster_fit_method !== "manual_override");
  const total = rows.length;

  // Load matched set PRZED petla
  const matchedSet = await loadMatchedPostIdsForMap(mapId);

  logPublic(
    PREFIX,
    `Start cluster fit — ${total} wpisow (concurrency=${concurrency}, matched_skip=${matchedSet.size})`,
  );
  options.onLog?.(
    "info",
    `▶ Cluster fit: ${total} wpisow do klasyfikacji (auto-matched shortcut dla ${matchedSet.size})`,
  );

  const stats: ClusterFitStats = {
    processed: 0,
    auto_matched: 0,
    llm_classified: 0,
    in_scope: 0,
    adjacent: 0,
    off_topic: 0,
    failed: 0,
    cost_usd: 0,
    tokens_in: 0,
    tokens_out: 0,
    duration_ms: 0,
  };

  const modelId = prompt.modelId || "gpt-4.1-mini";
  const temperature = prompt.temperature ?? 0.2;
  const nowIso = () => new Date().toISOString();

  // Split: auto_matched vs llm
  const autoRows = rows.filter((r) => matchedSet.has(r.id));
  const llmRows = rows.filter((r) => !matchedSet.has(r.id));

  // STEP 1 — auto_matched batch UPDATE
  for (const row of autoRows) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error: updErr } = await (supabase as any)
      .from("wp_posts")
      .update({
        cluster_fit: "in_scope",
        cluster_fit_confidence: 1.0,
        cluster_fit_reason:
          "Wpis zmatchowany przez SRO Matcher do noda mapy — automatycznie in_scope.",
        cluster_fit_method: "auto_matched",
        cluster_fit_classified_at: nowIso(),
      })
      .eq("id", row.id);
    if (updErr) {
      logError(PREFIX, `Auto-match update ${row.id} failed: ${updErr.message}`);
      stats.failed++;
    } else {
      stats.auto_matched++;
      stats.in_scope++;
    }
    stats.processed++;
  }

  if (autoRows.length > 0) {
    options.onLog?.(
      "info",
      `  Auto-matched ${autoRows.length} wpisow (L1 shortcut) -> in_scope (bez LLM)`,
    );
  }

  // STEP 2 — LLM dla pozostalych (chunked Promise.all, concurrency)
  for (let i = 0; i < llmRows.length; i += concurrency) {
    const chunk = llmRows.slice(i, i + concurrency);
    const results = await Promise.all(
      chunk.map((row) => classifyOne(row, ctx, prompt.template, modelId, temperature)),
    );

    for (let j = 0; j < chunk.length; j++) {
      const row = chunk[j];
      const r = results[j];
      stats.processed++;
      stats.cost_usd += r.cost_usd;
      stats.tokens_in += r.tokens_in;
      stats.tokens_out += r.tokens_out;

      if (r.success && r.classification) {
        stats.llm_classified++;
        if (r.classification === "in_scope") stats.in_scope++;
        else if (r.classification === "adjacent") stats.adjacent++;
        else stats.off_topic++;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error: updErr } = await (supabase as any)
          .from("wp_posts")
          .update({
            cluster_fit: r.classification,
            cluster_fit_confidence: r.confidence ?? null,
            cluster_fit_reason: r.reason ?? null,
            cluster_fit_method: "llm",
            cluster_fit_classified_at: nowIso(),
          })
          .eq("id", row.id);
        if (updErr) {
          logError(PREFIX, `LLM update ${row.id} failed: ${updErr.message}`);
        }
      } else {
        stats.failed++;
        logError(PREFIX, `Classify failed for ${row.id}: ${r.error}`);
      }
    }

    const pct = total > 0 ? Math.round((stats.processed / total) * 100) : 100;
    if (total > 0 && (stats.processed % 10 === 0 || stats.processed === total)) {
      const lastOk = results
        .map((r, idx) => ({ r, row: chunk[idx] }))
        .reverse()
        .find((x) => x.r.success);
      options.onLog?.(
        "info",
        `  Cluster fit ${pct}% (${stats.processed}/${total}) — in_scope=${stats.in_scope} adjacent=${stats.adjacent} off_topic=${stats.off_topic}, cost=$${stats.cost_usd.toFixed(4)}`,
        lastOk
          ? {
              sample_post_id: lastOk.row.id,
              sample_title: lastOk.row.title,
              sample_classification: lastOk.r.classification,
              sample_confidence: lastOk.r.confidence,
              sample_reason: lastOk.r.reason,
              compiled_prompt: lastOk.r.compiled_prompt,
              response_text: lastOk.r.response_text,
            }
          : undefined,
      );
      logDebug(PREFIX, `progress ${stats.processed}/${total}`);
    }
    options.onProgress?.(stats.processed, total, stats.cost_usd);
  }

  stats.duration_ms = Date.now() - t0;

  logPublic(
    PREFIX,
    `Cluster fit done ${stats.duration_ms}ms: auto=${stats.auto_matched} llm=${stats.llm_classified} (in_scope=${stats.in_scope} adjacent=${stats.adjacent} off_topic=${stats.off_topic}), failed=${stats.failed}, $${stats.cost_usd.toFixed(4)}`,
  );
  options.onLog?.(
    "stage",
    `✓ Cluster fit done ${(stats.duration_ms / 1000).toFixed(1)}s: auto=${stats.auto_matched} llm=${stats.llm_classified} · in_scope=${stats.in_scope} adjacent=${stats.adjacent} off_topic=${stats.off_topic} · cost=$${stats.cost_usd.toFixed(4)}`,
    { stats },
  );

  // Auto-recompute Decision Engine (non-blocking) — M15
  try {
    const { recomputeFinalActionsForWebsite } = await import("@/services/decisionEngine");
    options.onLog?.("info", "▶ Auto-recompute Decision Engine po klasyfikacji scope...");
    await recomputeFinalActionsForWebsite(ctx.websiteId, { onLog: options.onLog });
  } catch (err) {
    logError(PREFIX, `Auto-recompute Decision Engine failed: ${err instanceof Error ? err.message : String(err)}`);
  }

  // M16 auto-run: L5 coverage refresh (cluster_fit ma wpływ na coverage)
  try {
    const { auditMapCoverageOnly } = await import("@/services/validation/mapAuditOrchestrator");
    await auditMapCoverageOnly(mapId, { onLog: options.onLog });
  } catch (err) {
    logError(PREFIX, `Auto-recompute L5 coverage failed: ${err instanceof Error ? err.message : String(err)}`);
  }

  return stats;
}

/**
 * Klasyfikacja pojedynczego wpisu (rzadziej uzywane — gownie debug).
 */
export async function classifyClusterFitForWpPost(
  wpPostId: string,
  mapId: string,
): Promise<ClusterFitResultSingle> {
  const ctx = await loadMapContext(mapId);
  const prompt = await loadPrompt("wp_cluster_fit");
  if (!prompt) {
    return {
      success: false,
      error: "Prompt wp_cluster_fit nie jest aktywny w bazie",
      tokens_in: 0,
      tokens_out: 0,
      cost_usd: 0,
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: row } = await (supabase as any)
    .from("wp_posts")
    .select(
      "id, title, content_plaintext, extracted_entity, extracted_attribute, cluster_fit_method, cluster_fit_classified_at",
    )
    .eq("id", wpPostId)
    .maybeSingle();

  if (!row) {
    return {
      success: false,
      error: "Wpis nie istnieje",
      tokens_in: 0,
      tokens_out: 0,
      cost_usd: 0,
    };
  }

  if ((row as ClassifiableRow).cluster_fit_method === "manual_override") {
    return {
      success: false,
      error: "Wpis ma manual_override — odmowa reklasyfikacji",
      tokens_in: 0,
      tokens_out: 0,
      cost_usd: 0,
    };
  }

  // Auto-match shortcut dla single
  const matchedSet = await loadMatchedPostIdsForMap(mapId);
  if (matchedSet.has(wpPostId)) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (supabase as any)
      .from("wp_posts")
      .update({
        cluster_fit: "in_scope",
        cluster_fit_confidence: 1.0,
        cluster_fit_reason:
          "Wpis zmatchowany przez SRO Matcher do noda mapy — automatycznie in_scope.",
        cluster_fit_method: "auto_matched",
        cluster_fit_classified_at: new Date().toISOString(),
      })
      .eq("id", wpPostId);
    return {
      success: true,
      classification: "in_scope",
      confidence: 1.0,
      reason: "auto_matched",
      tokens_in: 0,
      tokens_out: 0,
      cost_usd: 0,
    };
  }

  const result = await classifyOne(
    row as ClassifiableRow,
    ctx,
    prompt.template,
    prompt.modelId || "gpt-4.1-mini",
    prompt.temperature ?? 0.2,
  );

  if (result.success && result.classification) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (supabase as any)
      .from("wp_posts")
      .update({
        cluster_fit: result.classification,
        cluster_fit_confidence: result.confidence ?? null,
        cluster_fit_reason: result.reason ?? null,
        cluster_fit_method: "llm",
        cluster_fit_classified_at: new Date().toISOString(),
      })
      .eq("id", wpPostId);
  }

  return result;
}

/**
 * Manual override klasyfikacji — user zmienia scope recznie.
 * method='manual_override' jest nietykalne przez kolejne runy LLM/auto_matched.
 */
export async function setClusterFitManualOverride(
  wpPostId: string,
  newValue: ClusterFitLabel,
  reason?: string,
): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase as any)
    .from("wp_posts")
    .update({
      cluster_fit: newValue,
      cluster_fit_confidence: 1.0,
      cluster_fit_reason: reason ?? "Manual override przez użytkownika",
      cluster_fit_method: "manual_override",
      cluster_fit_classified_at: new Date().toISOString(),
    })
    .eq("id", wpPostId);
  if (error) throw error;
}
